import React, { useCallback, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Element, Path } from 'slate';
import { AulaEntities, AulaEntity } from './Entities';
import { AulaEditorContext, ContentTemplate, EditorConfig } from './types.d';

const noop = () => {
  // If not replaced at runtime - this throws error intentionally
  throw new Error('noop');
};

const initialContext: AulaEditorContext = {
  entities: {},
  config: {
    filesUrl: '',
    sessionToken: '',
    accessToken: '',
    institution: '',
    apiUrl: '',
    userId: '',
    spaceId: '',
    assignmentId: '',
    conversationId: '',
    postId: '',
    materialId: '',
    ltiProviders: [],
    itemType: '',
    correlationId: '',
  },
  createEntity: noop,
  updateEntity: noop,
  deleteEntity: noop,
  draggingElement: null,
  setDraggingElement: noop,
  setRecordingPath: noop,
  recordingPath: null,
  setTemplatesPath: noop,
  templatesPath: null,
  templates: [],
};

const AulaEditorReactContext = React.createContext(initialContext);

export type ContextState = {
  entities: AulaEntities;
};

export type AulaEditorContextRef = React.RefObject<{
  context: ContextState;
  onChange: (state: ContextState) => void;
}>;

type ProviderProps = {
  config: EditorConfig;
  contextRef: AulaEditorContextRef;
  templates: ContentTemplate[];
  contentType?: string;
};

export const AulaEditorContextProvider: React.FunctionComponent<ProviderProps> = ({
  config,
  children,
  contextRef,
  templates,
  contentType,
}) => {
  const [isDragging, setIsDragging] = useState<Element | null>(null);
  const [recordingPath, setRecordingPath] = useState<Path | null>(null);
  const [templatesPath, setTemplatesPath] = useState<Path | null>(null);

  const updateEntities = useCallback(
    (_entities: AulaEntities) => {
      if (contextRef.current == null) {
        throw new Error('contextRef not initialized');
      }

      const { onChange, context } = contextRef.current;

      Object.entries(_entities).forEach(([key, value]) => {
        context.entities[key] = value;
      });

      onChange(context);
    },
    [contextRef]
  );

  const deleteEntity = useCallback(
    (id: string) => {
      if (contextRef.current == null) {
        throw new Error('contextRef not initialized');
      }

      const { onChange, context } = contextRef.current;

      delete context.entities[id];

      onChange(context);
    },
    [contextRef]
  );

  const createEntity = useCallback(
    (ent: AulaEntity) => {
      const id = uuidv4();

      if (contextRef.current == null) {
        throw new Error('contextRef not initialized');
      }

      updateEntities({
        [id]: ent,
      });

      return id;
    },
    [contextRef, updateEntities]
  );

  const updateEntity = useCallback(
    (id, val: Partial<AulaEntity>) => {
      if (contextRef.current == null) return null;

      const updatedEnt = {
        ...contextRef.current.context.entities?.[id],
        ...val,
      };

      updateEntities({ [id]: updatedEnt } as AulaEntities);

      return updatedEnt;
    },
    [contextRef, updateEntities]
  );

  if (contextRef.current == null) return null;

  const value = {
    config,
    entities: contextRef.current.context.entities,
    createEntity,
    updateEntity,
    deleteEntity,
    draggingElement: isDragging,
    setDraggingElement: setIsDragging,
    setRecordingPath,
    recordingPath,
    setTemplatesPath,
    templatesPath,
    templates,
    contentType,
  };

  return (
    <AulaEditorReactContext.Provider value={value}>
      {children}
    </AulaEditorReactContext.Provider>
  );
};

export const useAulaEditorContext = () => useContext(AulaEditorReactContext);
